import { supabase } from '@/integrations/supabase/client';

export interface ComparisonData {
  url1: string;
  url2: string;
  w?: number;
  h?: number;
  label1?: string;
  label2?: string;
  caption?: string;
  viewport1?: {
    zoom: number;
    panX: number;
    panY: number;
  };
  viewport2?: {
    zoom: number;
    panX: number;
    panY: number;
  };
  markers?: Array<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    label?: string;
  }>;
}

export interface SharedLink {
  id: string;
  short_id: string;
  link_type: 'before_and_after';
  comparison_data: ComparisonData;
  title?: string;
  slug?: string;
  before_version?: string;
  after_version?: string;
  campaign_name?: string;
  campaign_source?: string;
  campaign_medium?: string;
  campaign_content?: string;
  created_by?: string;
  is_active: boolean;
  expires_at?: string;
  require_login: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSharedLinkInput {
  comparison_data: ComparisonData;
  title?: string;
  slug?: string;
  before_version?: string;
  after_version?: string;
  campaign_name?: string;
  campaign_source?: string;
  campaign_medium?: string;
  campaign_content?: string;
  require_login?: boolean;
  expires_at?: string;
}

export interface UpdateSharedLinkInput {
  title?: string;
  comparison_data?: ComparisonData;
  campaign_name?: string;
  campaign_source?: string;
  campaign_medium?: string;
  campaign_content?: string;
  is_active?: boolean;
  require_login?: boolean;
  expires_at?: string;
}

/**
 * Fetch all shared links
 */
export async function getSharedLinks(): Promise<SharedLink[]> {
  try {
    const { data, error } = await supabase
      .from('app_shared_links')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching shared links:', error);
      throw new Error(`Failed to fetch shared links: ${error.message}`);
    }

    return data || [];
  } catch (error: any) {
    console.error('Error fetching shared links:', error);
    throw new Error(`Error fetching shared links: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get a single shared link by ID
 */
export async function getSharedLink(id: string): Promise<SharedLink | null> {
  try {
    const { data, error } = await supabase
      .from('app_shared_links')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching shared link:', error);
      throw new Error(`Failed to fetch shared link: ${error.message}`);
    }

    return data;
  } catch (error: any) {
    console.error('Error fetching shared link:', error);
    throw new Error(`Error fetching shared link: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get a shared link by short_id
 */
export async function getSharedLinkByShortId(shortId: string): Promise<SharedLink | null> {
  try {
    const { data, error } = await supabase
      .from('app_shared_links')
      .select('*')
      .eq('short_id', shortId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('Error fetching shared link:', error);
      throw new Error(`Failed to fetch shared link: ${error.message}`);
    }

    return data;
  } catch (error: any) {
    console.error('Error fetching shared link:', error);
    throw new Error(`Error fetching shared link: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Create a new shared link
 */
export async function createSharedLink(input: CreateSharedLinkInput): Promise<SharedLink> {
  try {
    console.log('Creating shared link:', input);

    const { data, error } = await supabase
      .from('app_shared_links')
      .insert({
        link_type: 'before_and_after',
        comparison_data: input.comparison_data,
        title: input.title,
        slug: input.slug,
        before_version: input.before_version,
        after_version: input.after_version,
        campaign_name: input.campaign_name,
        campaign_source: input.campaign_source,
        campaign_medium: input.campaign_medium,
        campaign_content: input.campaign_content,
        require_login: input.require_login ?? true,
        expires_at: input.expires_at,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating shared link:', error);
      throw new Error(`Failed to create shared link: ${error.message}`);
    }

    if (!data) {
      throw new Error('Failed to create shared link: No data returned');
    }

    console.log('Shared link created successfully:', data.id);
    return data;
  } catch (error: any) {
    console.error('Error creating shared link:', error);
    throw new Error(`Error creating shared link: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Update a shared link
 */
export async function updateSharedLink(id: string, input: UpdateSharedLinkInput): Promise<SharedLink> {
  try {
    console.log('Updating shared link:', id, input);

    const { data, error } = await supabase
      .from('app_shared_links')
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating shared link:', error);
      throw new Error(`Failed to update shared link: ${error.message}`);
    }

    if (!data) {
      throw new Error('Failed to update shared link: No data returned');
    }

    console.log('Shared link updated successfully:', data.id);
    return data;
  } catch (error: any) {
    console.error('Error updating shared link:', error);
    throw new Error(`Error updating shared link: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Delete a shared link
 */
export async function deleteSharedLink(id: string): Promise<void> {
  try {
    console.log('Deleting shared link:', id);

    const { error } = await supabase
      .from('app_shared_links')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting shared link:', error);
      throw new Error(`Failed to delete shared link: ${error.message}`);
    }

    console.log('Shared link deleted successfully:', id);
  } catch (error: any) {
    console.error('Error deleting shared link:', error);
    throw new Error(`Error deleting shared link: ${error.message || 'Unknown error'}`);
  }
}
